import { useState, useEffect } from "react";
import { Link } from "react-router";
import logo from "@/assets/logoLight.svg";
import { Instagram } from "@/assets/icons/Instagram";
import { Facebook } from "@/assets/icons/Facebook";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NAV_LINKS } from "@/constants/navigation";

const Footer = () => {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({
    navigation: true,
    contact: true,
  });

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 640;
      setIsMobile(mobile);
      if (!mobile) {
        setOpenSections({ navigation: true, contact: true });
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const toggleSection = (section: string) => {
    if (!isMobile) return;
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <footer className="w-full min-h-111.5 bg-main-1 flex">
      <div className="w-full max-w-360 mx-auto pt-14.5 pb-9 px-5 sm:px-10 md:px-20 flex flex-col gap-10">
        <div className="flex flex-col lg:flex-row justify-between gap-10">
          <div className="flex flex-col gap-6">
            <div>
              <img src={logo} alt="Logo" />
            </div>
            <p className="font-light text-sm text-white">
              {t("footer.slogan")}
            </p>
            <div className="flex items-center gap-5">
              <div className="w-7 h-7 shrink-0 cursor-pointer"
                onClick={() => window.open("https://www.instagram.com/profmonolith?igsh=MWRmN3B0M3l3N2U4", "_blank")}
              >
                <Instagram />
              </div>
              <div className="w-7 h-7 shrink-0 cursor-pointer"
                onClick={() => window.open("https://www.facebook.com/share/14h4E6tZCGD/?mibextid=wwXIfr", "_blank")}
              >
                <Facebook />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-10 lg:gap-[clamp(1rem,5vw,10rem)]">
            {/* Navigation Section */}
            <div className="flex flex-col gap-6">
              <div
                className="pb-2 flex items-center justify-between cursor-pointer sm:cursor-default border-b border-text-1 sm:border-none"
                onClick={() => toggleSection("navigation")}
              >
                <p className="font-medium text-2xl leading-8 text-white ">
                  {t("footer.navigation")}
                </p>
                {isMobile && (
                  <div className="text-white">
                    {openSections.navigation ? <ChevronUp size={34} /> : <ChevronDown size={34} />}
                  </div>
                )}
              </div>
              <AnimatePresence initial={false}>
                {openSections.navigation && (
                  <motion.nav
                    initial={isMobile ? { height: 0, opacity: 0 } : false}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <ul className="flex flex-col gap-5">
                      {NAV_LINKS.map((link) => (
                        <li key={link.to} className="pb-2 border-b border-text-1 sm:border-none">
                          <Link to={link.to} className="text-base text-white">
                            {t(link.label)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </motion.nav>
                )}
              </AnimatePresence>
            </div>

            {/* Contact Section */}
            <div className="flex flex-col gap-6">
              <div
                className="pb-2 flex items-center justify-between cursor-pointer sm:cursor-default border-b border-text-1 sm:border-none"
                onClick={() => toggleSection("contact")}
              >
                <p className="font-medium text-2xl leading-8 text-white ">
                  {t("footer.contact")}
                </p>
                {isMobile && (
                  <div className="text-white">
                    {openSections.contact ? <ChevronUp size={34} /> : <ChevronDown size={34} />}
                  </div>
                )}
              </div>
              <AnimatePresence initial={false}>
                {openSections.contact && (
                  <motion.div
                    initial={isMobile ? { height: 0, opacity: 0 } : false}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-col gap-2">
                      <a href="tel:+37499050488" className="text-base text-white pb-2 border-b border-text-1 sm:border-none">
                        +37499050488
                      </a>
                      <a
                        href="mailto:monolitarmenia@gmail.com"
                        className="text-base text-white pb-2 border-b border-text-1 sm:border-none break-all"
                      >
                        monolitarmenia@gmail.com
                      </a>
                      <a
                        href="https://maps.google.com/?q=Գրիբոյեդով 54/1"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-base text-white pb-2 border-b border-text-1 sm:border-none"
                      >
                      {t("footer.address_value")}
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="w-full h-px bg-text-1" />
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-2">
            <span className="font-medium text-base md:text-lg text-text-1 text-center sm:text-left">
              {t("copyright")}
            </span>
            <span className="font-medium text-base md:text-lg text-text-1 text-center sm:text-right">
              {t("footer.poweredBy")}{" "}
              <a
                href="https://granat.am"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline text-white font-medium"
              >
                {t("footer.developerName")}
              </a>
              {t("footer.poweredBySuffix")}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
